import React, { useState, useEffect } from 'react';
import { getAllCreatorRequests, updateCreatorRequest } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader, CheckCircle, XCircle, Clock, User, Mail, Phone, Link as LinkIcon } from 'lucide-react';

export const AdminCreatorRequestsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/');
      return;
    }
    fetchRequests();
  }, [user, filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await getAllCreatorRequests(filter);
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRequest = async (requestId, newStatus) => {
    if (!confirm(`Confirmer: ${newStatus === 'approved' ? 'Approuver' : 'Rejeter'} cette demande?`)) {
      return;
    }

    setProcessing(true);
    try {
      await updateCreatorRequest(requestId, newStatus, adminNotes);
      alert(`Demande ${newStatus === 'approved' ? 'approuvée' : 'rejetée'}!`);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchRequests();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || 'Opération échouée'));
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Approuvé</span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-sm font-medium flex items-center gap-1"><XCircle className="w-4 h-4" /> Rejeté</span>;
      default:
        return <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm font-medium flex items-center gap-1"><Clock className="w-4 h-4" /> En attente</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="container max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-foreground mb-8">
          Demandes Créateurs
        </h1>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            En attente
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'approved'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Approuvées
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'rejected'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Rejetées
          </button>
          <button
            onClick={() => setFilter('')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === ''
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent'
            }`}
          >
            Toutes
          </button>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-lg border border-border">
            <p className="text-muted-foreground">Aucune demande trouvée</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {request.username}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" /> {request.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" /> {request.phone_number}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        {getStatusBadge(request.status)}
                        <span className="px-3 py-1 bg-accent text-foreground rounded-full text-sm">
                          {request.content_type}
                        </span>
                      </div>
                    </div>
                  </div>
                  {request.status === 'pending' && (
                    <button
                      onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      {selectedRequest?.id === request.id ? 'Fermer' : 'Examiner'}
                    </button>
                  )}
                </div>

                {/* Details (always visible for approved/rejected) */}
                {(selectedRequest?.id === request.id || request.status !== 'pending') && (
                  <div className="border-t border-border pt-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Motivation</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {request.motivation}
                      </p>
                    </div>

                    {request.portfolio_url && (
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Portfolio</p>
                        <a
                          href={request.portfolio_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          <LinkIcon className="w-4 h-4" /> {request.portfolio_url}
                        </a>
                      </div>
                    )}

                    {request.admin_notes && (
                      <div className="bg-accent p-3 rounded-lg">
                        <p className="text-sm font-medium text-foreground mb-1">Note admin</p>
                        <p className="text-sm text-muted-foreground">{request.admin_notes}</p>
                      </div>
                    )}

                    {/* Action Panel for pending */}
                    {request.status === 'pending' && selectedRequest?.id === request.id && (
                      <div className="bg-accent p-4 rounded-lg space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Note administrative (optionnel)
                          </label>
                          <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground resize-none"
                            rows={3}
                            placeholder="Ajoutez une note pour l'utilisateur..."
                          />
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleUpdateRequest(request.id, 'approved')}
                            disabled={processing}
                            className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            {processing ? 'Traitement...' : 'Approuver'}
                          </button>
                          <button
                            onClick={() => handleUpdateRequest(request.id, 'rejected')}
                            disabled={processing}
                            className="flex-1 bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <XCircle className="w-5 h-5" />
                            {processing ? 'Traitement...' : 'Rejeter'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
